// schema.org data for the store, shared by the browser (useSeo) and the
// edge (functions/merch), so search engines see the same thing either way.
// Plain JS on purpose: the Pages Function bundles this file too.

export const SITE = "https://thecreaksofficial.com";
const BRAND = { "@type": "Brand", name: "The Creaks" };

const amount = (minor) => (minor / 100).toFixed(2);

// facts: { deliveryFromMinor, returnDays } — from the store config.
function offerExtras(facts) {
  const extras = {};
  if (facts?.returnDays) {
    extras.hasMerchantReturnPolicy = {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "CZ",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: facts.returnDays,
      returnMethod: "https://schema.org/ReturnByMail",
    };
  }
  if (facts?.deliveryFromMinor != null) {
    extras.shippingDetails = {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: amount(facts.deliveryFromMinor), currency: "CZK" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "CZ" },
    };
  }
  return extras;
}

export function productLd(p, facts) {
  const url = `${SITE}/merch/${p.slug}`;
  const extras = offerExtras(facts);
  const offers = p.variants.map((v) => ({
    "@type": "Offer",
    sku: v.id,
    name: v.name,
    price: amount(v.priceMinor),
    priceCurrency: v.currency || "CZK",
    availability: p.teased ? "https://schema.org/PreOrder" : v.inStock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
    itemCondition: "https://schema.org/NewCondition",
    url,
    ...extras,
  }));
  const prices = p.variants.map((v) => v.priceMinor);
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.title,
    description: p.seo?.description || p.description || p.story || undefined,
    image: p.images.map((i) => i.url),
    url,
    brand: BRAND,
    category: p.category?.name,
    offers:
      offers.length === 1
        ? offers[0]
        : {
            "@type": "AggregateOffer",
            lowPrice: amount(Math.min(...prices)),
            highPrice: amount(Math.max(...prices)),
            priceCurrency: "CZK",
            offerCount: offers.length,
            offers,
          },
  };
}

export function breadcrumbLd(p) {
  const items = [{ name: "Merch", item: `${SITE}/merch` }];
  if (p.category) items.push({ name: p.category.name, item: `${SITE}/merch?c=${p.category.slug}` });
  items.push({ name: p.title, item: `${SITE}/merch/${p.slug}` });
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((x, i) => ({ "@type": "ListItem", position: i + 1, ...x })),
  };
}

export function itemListLd(products) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 30).map((p, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE}/merch/${p.slug}`, name: p.title })),
  };
}

// Delivery facts from the public store config.
export function factsFrom(config) {
  const home = (config?.countries || []).find((c) => c.code === "CZ") || config?.countries?.[0];
  const prices = (home?.methods || []).map((m) => m.priceMinor).filter((n) => n != null);
  return { deliveryFromMinor: prices.length ? Math.min(...prices) : null, returnDays: config?.returnWindowDays || 14 };
}
